import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { isDirectorUser as isDirectorUserUtil } from '@/modules/login/login-utils';
import { usePreferences } from '@/react-query/usePreferences';
import { useRestrictClinicalInteractionsForDirectors } from '@/utils/hooks';

import type { Appointment } from '../../../../../react-query/useAppointments';
import type {
  GetTaskResponse,
  Task,
  TaskStatus,
} from '../../../../../react-query/useGetTasks';
import { useUpdateTaskStatus } from '../../../../../react-query/useGetTasks';
import { useSearchParams } from '../../../../../utils/hooks/useSearchParams';
import { mixpanelEvents, useTrackEvent } from '../../../../../utils/mixpanel';

import { GamifiedTaskRedesigned } from './gamifiedTaskRedesigned';

interface GamifiedTasksRedesignedProps {
  appointment: Appointment;
  context?: 'PATIENT_STRIP' | 'APPOINTMENT_MODAL' | 'PATIENT_SHEET';
  patientTask?: GetTaskResponse;
}

export const GamifiedTasksRedesigned = ({
  appointment,
  context,
  patientTask,
}: GamifiedTasksRedesignedProps) => {
  const { trackEvent } = useTrackEvent();
  const {
    mutate: updateTaskStatus,
    isLoading,
    variables,
  } = useUpdateTaskStatus();
  const searchParams = useSearchParams();
  const selectedDateParam = searchParams.get('selectedDateParam');
  const isRestrictClinicalInteractions =
    useRestrictClinicalInteractionsForDirectors();

  const handleUpdateTask = useCallback(
    (task: Task, status: TaskStatus) => {
      if (!patientTask || !task.id) return;

      updateTaskStatus({
        operatoryId: appointment.appointment.operatory?.id,
        status,
        taskId: task.id,
        unifiedAppointmentId: patientTask.unifiedAppointmentId,
      });

      trackEvent(mixpanelEvents.userTaskInteraction, {
        appointmentId: appointment.appointment.unifiedId,
        context,
        imagingPatientId: appointment.patient.imagingPatientId,
        pmsPatientId: appointment.patient.pmsPatientId,
        selectedDate: selectedDateParam,
        status,
        taskId: task.id,
        type: task.type,
        unifiedId: appointment.appointment.unifiedId,
        pmsAppointmentId: appointment.appointment.pmsAppointmentId,
        estimatedVisitId: appointment.appointment.estimatedVisitId,
      });
    },
    [
      appointment.appointment.estimatedVisitId,
      appointment.appointment.operatory?.id,
      appointment.appointment.pmsAppointmentId,
      appointment.appointment.unifiedId,
      appointment.patient.imagingPatientId,
      appointment.patient.pmsPatientId,
      context,
      patientTask,
      selectedDateParam,
      trackEvent,
      updateTaskStatus,
    ],
  );

  function sortTasksByStatus(tasks: any[] | undefined) {
    const statusesToDeprioritise = ['REJECTED', 'COMPLETED', 'ACKNOWLEDGED'];

    return tasks?.sort((a, b) => {
      const isADeprioritised = statusesToDeprioritise.includes(a.status);
      const isBDeprioritised = statusesToDeprioritise.includes(b.status);

      if (isADeprioritised && !isBDeprioritised) return 1;
      if (!isADeprioritised && isBDeprioritised) return -1;
      return 0;
    });
  }
  const sortedTasks = sortTasksByStatus(patientTask?.tasks);

  return (
    <>
      {sortedTasks?.map((task: Task) => (
        <GamifiedTaskRedesigned
          disabled={isRestrictClinicalInteractions}
          isLoading={isLoading && variables?.taskId === task.id}
          key={task.type}
          onClick={handleUpdateTask}
          task={task}
        />
      ))}
    </>
  );
};
